import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useReviewStyles } from './Styles';
import { fetchPDDocumentReviewAPI, downloadPDCustomFileAPI } from './batchDocumentsAPI';
import { formatDisplayDate, padCount } from './formatters';

/**
 * The two-page (split-screen) review window — Documents Tab FSD §4.
 *
 * "Clicking the orange icon on a document row opens a two-page (split-screen)
 * window for that document. The right-hand pane displays the original source
 * PDF. The left-hand pane displays the digitalized fields extracted from that
 * document, positioned for side-by-side comparison and review against the PDF."
 * (BR-DOC-REVIEW-02 fixes that left/right assignment, so the two panes are not
 * swappable.) BR-DOC-REVIEW-01 makes the window shared across every Documents
 * tab section, which is why this takes only a document id and resolves
 * everything else itself — nothing here is Deviations- or CC-specific.
 *
 * READ-ONLY. The FSD is explicit that the review ACTIONS are not settled yet:
 * "Exact review actions available within the window (e.g., editing a field,
 * marking a field as reviewed, approving/rejecting extracted values) are not yet
 * confirmed." So this presents the comparison and nothing that writes — no Edit,
 * no per-field checkbox, no Save. The pieces a save flow will need are already
 * in place behind it (the API returns each field's confidence and `reviewed`
 * state, and pdExtractedFields derives the counts from kvpJsonEdited), so
 * enabling editing later is additive rather than a rewrite.
 *
 * Rendered as a fullScreen MUI Dialog rather than a routed page, matching how
 * Document Repository already opens from inside the batch tabs — the PD
 * full-screen views are deliberately URL-less (see ProjectDevNavContext). That
 * also means this one component can serve the Document Repository's own
 * currently-disabled "Review fields" row action unchanged.
 */

// Band edges for the confidence chip, defaulting to the same 80/90 the
// commercial review screen falls back to (CommonDigitizedDocument.jsx) when
// org_level_settings has no row for the ecosystem.
const DEFAULT_BANDS = { low: 80, high: 90 };

// Ported from CommonDigitizedDocument's getLevelColor, named colours included,
// so a given score paints identically on both screens.
const levelColourOf = (confidence, bands) => {
  if (confidence == null) return '#cccccc';
  if (confidence < bands.low) return 'red';
  if (confidence <= bands.high) return 'orange';
  return 'green';
};

const badgeStyle = (background) => ({
  backgroundColor: background,
  color: 'white',
  borderRadius: 4,
  padding: '2px 8px',
  fontSize: '0.75rem',
  fontWeight: 600,
  minWidth: 24,
  textAlign: 'center',
});

const legendChipStyle = (background, minWidth) => ({
  backgroundColor: background,
  color: 'white',
  borderRadius: 4,
  padding: '4px 12px',
  fontSize: '0.75rem',
  fontWeight: 600,
  textAlign: 'center',
  minWidth,
});

function resolveBands(confidenceBands) {
  const low = Number(
    confidenceBands?.lowConfidenceLimit ?? confidenceBands?.lowconfidencelimit
  );
  const high = Number(
    confidenceBands?.highConfidenceLimit ?? confidenceBands?.highconfidencelimit
  );
  return {
    low: Number.isFinite(low) ? low : DEFAULT_BANDS.low,
    high: Number.isFinite(high) ? high : DEFAULT_BANDS.high,
  };
}

const DASH = '—';

export default function ReviewDocumentWindow({ open, documentId, documentLabel, onClose }) {
  const classes = useReviewStyles();

  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  // Right pane. The PDF is fetched separately from the field data so the window
  // paints immediately and the (much larger) file streams in behind it.
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  // Blob URLs are revoked on close/change; held in a ref so the cleanup does not
  // depend on state that may already have been replaced.
  const pdfUrlRef = useRef(null);

  const releasePdf = () => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    setPdfUrl(null);
  };

  // ---- field data ---------------------------------------------------------
  useEffect(() => {
    if (!open || !documentId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPayload(null);
    (async () => {
      const res = await fetchPDDocumentReviewAPI(documentId);
      if (cancelled) return;
      if (res?.status && res.data) {
        setPayload(res.data);
      } else {
        setError(res?.message || 'Unable to load this document for review.');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, documentId]);

  // ---- source PDF ---------------------------------------------------------
  const customFileId = payload?.document?.collabrixPDCustomFileID;
  useEffect(() => {
    if (!open || customFileId == null) return undefined;
    let cancelled = false;
    setPdfLoading(true);
    setPdfError(null);
    (async () => {
      const res = await downloadPDCustomFileAPI(customFileId);
      if (cancelled) return;
      const bytes = res?.status && res?.fileData?.data;
      if (!bytes) {
        setPdfError('The source document could not be loaded.');
        setPdfLoading(false);
        return;
      }
      const url = URL.createObjectURL(
        new Blob([new Uint8Array(bytes)], { type: 'application/pdf' })
      );
      pdfUrlRef.current = url;
      setPdfUrl(url);
      setPdfLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, customFileId]);

  // Release the blob when the window closes, so reopening always re-fetches
  // rather than showing a stale (or already-revoked) object URL.
  useEffect(() => {
    if (!open) {
      releasePdf();
      setPdfError(null);
      setPdfLoading(false);
    }
  }, [open]);

  // Unmount cleanup — a fullScreen Dialog can be unmounted while still open
  // (e.g. the parent tab switches away), which the !open effect above misses.
  useEffect(() => () => releasePdf(), []);

  const bands = useMemo(() => resolveBands(payload?.confidenceBands), [payload]);

  const doc = payload?.document || null;
  const fields = payload?.fields || [];

  // FSD: the window is titled by the document it was opened for. documentLabel
  // is what the row showed, so the title stays stable while the fetch is in
  // flight instead of appearing only once data lands.
  const title = doc
    ? `${doc.documentType || 'Document'}${doc.siteName ? ` (${doc.siteName})` : ''}`
    : documentLabel || 'Review Digitalized Document';

  const handleDownload = async () => {
    if (customFileId == null || downloading) return;
    setDownloading(true);
    try {
      const res = await downloadPDCustomFileAPI(customFileId);
      if (!res?.status || !res?.fileData?.data) {
        console.error('[ReviewDocumentWindow] Download failed for', documentId);
        return;
      }
      const a = window.document.createElement('a');
      a.href = URL.createObjectURL(
        new Blob([new Uint8Array(res.fileData.data)], { type: 'application/octet-stream' })
      );
      a.download = res.fileName || doc?.fileName || 'document.pdf';
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } finally {
      setDownloading(false);
    }
  };

  const contextItems = doc
    ? [
        ['CMO - Site', doc.siteName],
        ['Project ID', doc.projectId],
        ['Campaign ID', doc.campaignId],
        ['CMO Product Code', doc.productCode],
        ['Stage', doc.stage],
        ['Batch Number', doc.batchNumber],
        ['Version', doc.version],
        ['Upload Date', formatDisplayDate(doc.uploadDate)],
      ]
    : [];

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullScreen
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="pd-review-document-title"
    >
      {/* Header, pane titles and field rows use the same MUI primitives and
          sizes as Digitisation/CommonDigitizedDocument. */}
      <div className={classes.header}>
        <Button
          startIcon={<ChevronLeftIcon fontSize="small" />}
          style={{ fontWeight: 'bold', textTransform: 'none', marginLeft: 0 }}
          onClick={onClose}
        >
          Back
        </Button>
        <Typography id="pd-review-document-title" variant="h6">
          {title}
        </Typography>
      </div>
      <Divider style={{ backgroundColor: '#707070', height: 1 }} />

      {loading ? (
        <div className={classes.pdfOverlay} style={{ position: 'static', flex: 1 }}>
          <CircularProgress size={26} />
          <span>Loading digitalized fields…</span>
        </div>
      ) : error ? (
        <div className={classes.pdfOverlay} style={{ position: 'static', flex: 1 }}>
          <span>{error}</span>
        </div>
      ) : (
        <>
          {contextItems.some(([, value]) => value) && (
            <div className={classes.contextBar}>
              {contextItems
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <div className={classes.contextItem} key={label}>
                    <Typography variant="body2" component="span" className={classes.contextLabel}>
                      {label}
                    </Typography>
                    <Typography variant="body2" component="span" className={classes.contextValue}>
                      {value}
                    </Typography>
                  </div>
                ))}
            </div>
          )}

          <div className={classes.panes}>
            {/* LEFT — digitalized fields (BR-DOC-REVIEW-02) */}
            <div className={classes.pane}>
              <div className={classes.paneHeader}>
                <Typography variant="h6">
                  {doc?.documentType ? `${doc.documentType} Data` : 'Digitalized Fields'}
                </Typography>
              </div>

              <div className={classes.fieldsBox}>
                {fields.length === 0 ? (
                  <div className={classes.empty}>
                    No digitalized fields were extracted from this document.
                  </div>
                ) : (
                  fields.map((field, index) => (
                    <Grid
                      container
                      alignItems="center"
                      spacing={2}
                      className={classes.formField}
                      key={`${field.label}-${index}`}
                    >
                      <Grid item xs={4}>
                        <Typography style={{ paddingRight: 16, fontWeight: 500 }}>
                          {field.label}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <TextField
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={field.value || DASH}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box display="flex" alignItems="center" pl={1}>
                                  <Box
                                    style={{
                                      width: 1,
                                      height: 39,
                                      backgroundColor: '#ccc',
                                      marginRight: 3,
                                    }}
                                  />
                                  <Box style={badgeStyle(levelColourOf(field.confidence, bands))}>
                                    {field.confidence == null ? DASH : Math.round(field.confidence)}
                                  </Box>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  ))
                )}
              </div>

              <Box display="flex" alignItems="center" gap={2} mb={2} mt={3}>
                <Typography variant="body2" color="textSecondary">
                  Confidence Levels Legend:
                </Typography>
                <Box style={legendChipStyle('green', 30)}>{`> ${bands.high}`}</Box>
                <Box style={{ ...legendChipStyle('orange', 50), marginLeft: 16 }}>
                  {`${bands.low} - ${bands.high}`}
                </Box>
                <Box style={{ ...legendChipStyle('red', 30), marginLeft: 16 }}>
                  {`< ${bands.low}`}
                </Box>
              </Box>

              {/* Both lines go through Typography for the same reason as the
                  context strip above: one font stack and line height across the
                  whole pane, with weight and colour carrying the hierarchy. */}
              {doc && (
                <Typography variant="body2" component="div" className={classes.reviewSummary}>
                  {`Fields that require review - ${padCount(doc.fieldsRequiringReview)}`}
                  {' / '}
                  {`Fields reviewed - ${doc.fieldsReviewed}`}
                </Typography>
              )}
              <Typography variant="body2" component="div" className={classes.readOnlyNote}>
                Review actions (editing a field, marking a field as reviewed) are not
                enabled yet — this view is for comparison against the source document.
              </Typography>
            </div>

            {/* RIGHT — source PDF (BR-DOC-REVIEW-02) */}
            <div className={classes.pane}>
              <div className={classes.paneHeader}>
                <Typography variant="h6">PDF View</Typography>
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  className={classes.downloadButton}
                  startIcon={<GetAppIcon />}
                  onClick={handleDownload}
                  disabled={customFileId == null || downloading}
                >
                  {downloading ? 'Downloading…' : 'Download'}
                </Button>
              </div>

              <div className={classes.pdfBox}>
                {pdfUrl && (
                  <iframe
                    src={pdfUrl}
                    className={classes.pdfFrame}
                    title={`Source document – ${doc?.fileName || documentId}`}
                  />
                )}
                {(pdfLoading || pdfError || (!pdfUrl && !pdfLoading)) && (
                  <div className={classes.pdfOverlay}>
                    {pdfLoading ? (
                      <>
                        <CircularProgress size={26} />
                        <span>Loading source document…</span>
                      </>
                    ) : (
                      <span>
                        {pdfError ||
                          'No source document is attached to this record.'}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
}

ReviewDocumentWindow.propTypes = {
  open: PropTypes.bool,
  // Mosymphony ID of the pd_digitized_extracted_data row ("MSY-00000123").
  documentId: PropTypes.string,
  // What the row showed, used as the window title until the fetch resolves.
  documentLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

ReviewDocumentWindow.defaultProps = {
  open: false,
  documentId: null,
  documentLabel: '',
};
